import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField/TextField";
import { Formik, Form } from "formik";
import FormikSelect, { TOption, TOptionType } from "./FormikSelect";
import { EQUAL_TO, NOT_EQUAL_TO } from "./Constants";
import DialogButtons from "./DialogButtons";
import { useTranslation } from "react-i18next";

export { EQUAL_TO };
export type TStateOperator = typeof EQUAL_TO | typeof NOT_EQUAL_TO;

const operatorOptions: TOption<string>[] = [
  { value: EQUAL_TO },
  { value: NOT_EQUAL_TO },
];

interface IStateConditionDialogProps {
  title: string;
  operator: TStateOperator;
  state: string;
  stateOptions: TOption<TOptionType>[];
  open: boolean;
  onClose: (operator: TStateOperator, state: string) => void;
}

const StateConditionDialog = (props: IStateConditionDialogProps) => {
  const { title, operator, state, stateOptions, open, onClose } = props;
  const { t } = useTranslation();

  return (
    <Dialog open={open} fullWidth data-cy="state-picker-condition-dialog">
      <DialogTitle>{title}</DialogTitle>
      <Formik
        initialValues={{ operator: operator, state: state }}
        onSubmit={(values) => {
          onClose(values.operator, values.state);
        }}
      >
        <Form>
          <DialogContent>
            <Box gap={1} display="flex" flexDirection="row">
              <FormikSelect
                label={t("Operator")}
                name="operator"
                options={operatorOptions}
                style={{ width: "40%" }}
                margin="none"
                data-cy="state-picker-select-operator"
              />
              <FormikSelect
                label={t("State")}
                name="state"
                options={stateOptions}
                style={{ width: "60%" }}
                margin="none"
                data-cy="state-picker-select-state"
              />
            </Box>
          </DialogContent>
          <DialogButtons
            submitButtonLabel="Save"
            resetCallback={() => onClose(operator, state)}
          />
        </Form>
      </Formik>
    </Dialog>
  );
};

type TStateConditionPickerProps = {
  label: string;
  operator: TStateOperator;
  state: string;
  stateOptions: TOption<TOptionType>[];
  onConditionChange: (operator: TStateOperator, state: string) => void;
};

const StateConditionPicker = (
  props: TStateConditionPickerProps & TextFieldProps
) => {
  const {
    label,
    operator,
    state,
    stateOptions,
    onConditionChange,
    ...textFieldProps
  } = props;
  const [condition, setCondition] = useState<string>(operator + " " + state);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setCondition(operator + " " + state);
  }, [operator, state]);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = (operator: TStateOperator, state: string) => {
    setOpen(false);
    setCondition(operator + " " + state);
    onConditionChange(operator, state);
  };

  return (
    <>
      <TextField
        {...textFieldProps}
        label={label}
        value={condition}
        InputProps={{
          readOnly: true,
        }}
        onClick={handleClick}
      />
      <StateConditionDialog
        title={label}
        operator={operator}
        state={state}
        stateOptions={stateOptions}
        open={open}
        onClose={handleClose}
      />
    </>
  );
};

export default StateConditionPicker;
